import { createContext, useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import api from "../service/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AxiosError } from "axios";

interface IUserProviderProps {
  children: ReactNode;
}

interface IUserLogin {
  email: string;
  password: string;
}

interface ITechs {
  id: string;
  title: string;
  status: string;
}

interface IUser {
  id: string;
  name: string;
  email: string;
  course_module: string;
  bio: string;
  contact: string;
  techs: ITechs[];
}

interface IError {
  status: string;
  message: string[];
}

interface IUserProviderData {
  handleLogin: (data: IUserLogin) => void;
  handleLogout: () => void;
  handleRegister: (data: IUser) => void;
  user: IUser | undefined;
  setUser: Function;
  loading: boolean;
  setLoading: Function;
  userTechsList: ITechs[];
  setUserTechsList: Function;
  showLogoutModal: boolean;
  setShowModalLogout: Function;
}

export const UserContext = createContext<IUserProviderData>(
  {} as IUserProviderData
);

export const UserProvider = ({ children }: IUserProviderProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState({} as IUser | undefined);
  const [loading, setLoading] = useState(true);
  const [userTechsList, setUserTechsList] = useState([]);
  const [showLogoutModal, setShowModalLogout] = useState(false);

  const handleLogin = (data: IUserLogin) => {
    api
      .post("/sessions", data)
      .then((response) => {
        const { token, user } = response.data;

        setUser(user);
        setUserTechsList(user.techs);

        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        localStorage.clear();
        localStorage.setItem("@token", token);
        localStorage.setItem("@userID", user.id);

        navigate("/dashboard");
      })
      .catch((err) => {
        console.log(err);
        toast.error("Email ou senha inválidos", {
          theme: "dark",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("@token");

      if (token) {
        try {
          api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          const { data } = await api.get("/profile");
          setUser(data);
          setUserTechsList(data.techs);
        } catch (error) {
          localStorage.clear();
        }
      }

      setLoading(false);
    };

    loadUser();
  }, []);

  const handleLogout = () => {
    setShowModalLogout(false);
    setUser(undefined);
    localStorage.clear();
    navigate("/", { replace: true });
  };

  const handleRegister = async (data: IUser) => {
    setLoading(true);

    try {
      await api.post("/users", data);

      toast.success("Conta criada com sucesso!", {
        theme: "dark",
      });

      navigate("/");
    } catch (error) {
      const err = error as AxiosError<IError>;
      console.log(err);

      toast.error(`${err.response?.data.message}`, {
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserContext.Provider
      value={{
        handleLogin,
        handleLogout,
        handleRegister,
        user,
        setUser,
        loading,
        setLoading,
        userTechsList,
        setUserTechsList,
        showLogoutModal,
        setShowModalLogout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
